import { useState, useCallback, useEffect } from 'react';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';
import { Card, CardContent, Button, cn } from '@pantry/ui';
import {
  useItemInstances,
  type ItemWithInstanceCounts,
  type ItemInstance,
  type ItemUnit,
} from '@pantry/api-client';
import { QuantityActionDialog } from '../items/quantity-action-dialog';
import { type Product, getDaysUntilExpiry } from '@pantry/shared';
import { useResolveProductName } from '../../hooks/useProductLanguage';
import { ConsumeReasonDialog } from '../items/consume-reason-dialog';
import { WasteReasonDialog } from '../items/waste-reason-dialog';
import { ArchiveBoxIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

// ─── formatExpiryText ────────────────────────────────────────────────────────

function formatExpiryText(
  days: number | null,
  t: ReturnType<typeof useLingui>['t']
): React.ReactNode {
  if (days === null) return t`Loading...`;

  if (days < 0) {
    const absDays = Math.abs(days);
    return <span className="text-expired">{t`Expired ${absDays} days ago`}</span>;
  }

  if (days === 0) {
    return <span className="text-expiring-today font-medium">{t`Expires today`}</span>;
  }

  if (days === 1) {
    return <span className="text-expiring-today">{t`Expires tomorrow`}</span>;
  }

  return <span className="text-expiring-soon">{t`Expires in ${days} days`}</span>;
}

// ─── ViewMoreLink ─────────────────────────────────────────────────────────────

function ViewMoreLink({ count }: { count: number }) {
  const { t } = useLingui();
  return (
    <Link to="/items" className="block text-sm text-primary hover:underline text-center py-2">
      {t`View ${count} more...`}
    </Link>
  );
}

// ─── ExpiringItemRow ─────────────────────────────────────────────────────────

function ExpiringItemRow({
  item,
  householdId,
  onInstancesLoaded,
  index,
  isDemoMode = false,
  showItemActionsTour = false,
}: {
  item: ItemWithInstanceCounts;
  householdId: string;
  onInstancesLoaded?: (itemId: string, instances: ItemInstance[]) => void;
  index: number;
  isDemoMode?: boolean;
  showItemActionsTour?: boolean;
}) {
  const { t } = useLingui();
  // Skip fetching instances for demo items
  const { data: instances } = useItemInstances(
    isDemoMode ? '' : householdId,
    isDemoMode ? '' : item.id
  );
  const [showConsumeDialog, setShowConsumeDialog] = useState(false);
  const [showWasteDialog, setShowWasteDialog] = useState(false);

  const activeInstances = (instances ?? [])
    .filter((i) => i.status === 'active')
    .sort((a, b) => {
      if (!a.expiryDate && !b.expiryDate) return 0;
      if (!a.expiryDate) return 1;
      if (!b.expiryDate) return -1;
      return a.expiryDate.localeCompare(b.expiryDate);
    });

  useEffect(() => {
    if (instances && onInstancesLoaded && !isDemoMode) {
      onInstancesLoaded(item.id, instances);
    }
  }, [instances, item.id, onInstancesLoaded, isDemoMode]);

  const resolveProductName = useResolveProductName();

  const expiryDate = item.earliestExpiryDate;
  const daysUntilExpiry = expiryDate ? getDaysUntilExpiry(expiryDate) : null;

  const itemId = item.id.slice(0, 8);
  const itemName =
    item.customName ||
    (item.product ? resolveProductName(item.product as Product) : null) ||
    t`Item #${itemId}`;
  const hasActiveInstances = activeInstances.length > 0;
  const hasMultipleInstances = activeInstances.length > 1;

  return (
    <>
      <div
        className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors animate-in"
        style={{ animationDelay: `${index * 50}ms` }}
      >
        {/* Product image or icon */}
        <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
          {item.product?.imageUrl ? (
            <img src={item.product.imageUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <ArchiveBoxIcon className="h-5 w-5 text-muted-foreground" />
          )}
        </div>

        {/* Item info */}
        {isDemoMode ? (
          <div className="flex-1 min-w-0">
            <p className="font-medium text-foreground truncate">{itemName}</p>
            <p className="text-sm text-muted-foreground">
              {formatExpiryText(daysUntilExpiry, t)}
              {item.activeQuantity && item.activeQuantity > 1 && (
                <span className="ml-2 text-xs bg-muted px-1.5 py-0.5 rounded">
                  {t`Qty: ${item.activeQuantity}`}
                </span>
              )}
            </p>
          </div>
        ) : (
          <Link to="/items/$itemId" params={{ itemId: item.id }} className="flex-1 min-w-0">
            <p className="font-medium text-foreground truncate hover:text-primary transition-colors">
              {itemName}
            </p>
            <p className="text-sm text-muted-foreground">
              {formatExpiryText(daysUntilExpiry, t)}
              {item.activeQuantity && item.activeQuantity > 1 && (
                <span className="ml-2 text-xs bg-muted px-1.5 py-0.5 rounded">
                  {t`Qty: ${item.activeQuantity}`}
                </span>
              )}
            </p>
          </Link>
        )}

        {/* Actions */}
        <div
          className="flex gap-1 flex-shrink-0"
          data-tour={showItemActionsTour ? 'item-actions' : undefined}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowConsumeDialog(true)}
            disabled={isDemoMode || !hasActiveInstances}
            className="h-8 w-8 p-0 text-fresh hover:text-fresh hover:bg-fresh/10"
          >
            <CheckIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowWasteDialog(true)}
            disabled={isDemoMode || !hasActiveInstances}
            className="h-8 w-8 p-0 text-expired hover:text-expired hover:bg-expired/10"
          >
            <XMarkIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Dialogs */}
      {showConsumeDialog &&
        hasActiveInstances &&
        (hasMultipleInstances ? (
          <QuantityActionDialog
            householdId={householdId}
            itemId={item.id}
            instances={activeInstances}
            unit={item.unit}
            action="consume"
            onClose={() => setShowConsumeDialog(false)}
          />
        ) : (
          <ConsumeReasonDialog
            householdId={householdId}
            itemId={item.id}
            instanceId={activeInstances[0].id}
            instanceQuantity={activeInstances[0].quantity}
            unit={item.unit}
            onClose={() => setShowConsumeDialog(false)}
          />
        ))}

      {showWasteDialog &&
        hasActiveInstances &&
        (hasMultipleInstances ? (
          <QuantityActionDialog
            householdId={householdId}
            itemId={item.id}
            instances={activeInstances}
            unit={item.unit}
            action="waste"
            onClose={() => setShowWasteDialog(false)}
          />
        ) : (
          <WasteReasonDialog
            householdId={householdId}
            itemId={item.id}
            instanceId={activeInstances[0].id}
            instanceQuantity={activeInstances[0].quantity}
            unit={item.unit}
            onClose={() => setShowWasteDialog(false)}
          />
        ))}
    </>
  );
}

// ─── TimelineSection ──────────────────────────────────────────────────────────

function TimelineSection({
  title,
  items,
  householdId,
  onInstancesLoaded,
  variant,
  isDemoMode = false,
  isFirstSection = false,
}: {
  title: string;
  items: ItemWithInstanceCounts[];
  householdId: string;
  onInstancesLoaded: (itemId: string, instances: ItemInstance[]) => void;
  variant: 'expired' | 'today' | 'soon';
  isDemoMode?: boolean;
  isFirstSection?: boolean;
}) {
  const borderColor =
    variant === 'expired'
      ? 'border-l-expired'
      : variant === 'today'
        ? 'border-l-expiring-today'
        : 'border-l-expiring-soon';

  const dotColor =
    variant === 'expired'
      ? 'bg-expired'
      : variant === 'today'
        ? 'bg-expiring-today'
        : 'bg-expiring-soon';

  return (
    <Card
      className={cn('border-l-4', borderColor)}
      data-tour={isFirstSection ? 'expiring-group' : undefined}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className={cn('h-2 w-2 rounded-full', dotColor)} />
          <span className="text-sm font-medium text-muted-foreground">{title}</span>
          <span className="text-xs text-muted-foreground">
            (<Plural value={items.length} one="# item" other="# items" />)
          </span>
        </div>
        <div className="space-y-2">
          {items.slice(0, 5).map((item, index) => (
            <ExpiringItemRow
              key={item.id}
              item={item}
              householdId={householdId}
              onInstancesLoaded={onInstancesLoaded}
              index={index}
              isDemoMode={isDemoMode}
              showItemActionsTour={isFirstSection && index === 0}
            />
          ))}
          {items.length > 5 && <ViewMoreLink count={items.length - 5} />}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── ExpiringSoonTimeline ─────────────────────────────────────────────────────

export function ExpiringSoonTimeline({
  expiringItems,
  isLoading,
  error,
  householdId,
  isDemoMode = false,
  dashboardExpiryDays,
}: {
  expiringItems: ItemWithInstanceCounts[];
  isLoading: boolean;
  error: Error | null;
  householdId: string;
  isDemoMode?: boolean;
  dashboardExpiryDays?: number;
}) {
  const { t } = useLingui();
  const [showBulkConsumeDialog, setShowBulkConsumeDialog] = useState(false);
  const [showBulkWasteDialog, setShowBulkWasteDialog] = useState(false);
  const [bulkInstances, setBulkInstances] = useState<{
    instances: ItemInstance[];
    itemId: string;
    unit: ItemUnit;
  } | null>(null);

  const [allInstancesMap, setAllInstancesMap] = useState<Map<string, ItemInstance[]>>(new Map());

  const allActiveInstances = Array.from(allInstancesMap.values())
    .flat()
    .filter((i) => i.status === 'active')
    .sort((a, b) => {
      if (!a.expiryDate && !b.expiryDate) return 0;
      if (!a.expiryDate) return 1;
      if (!b.expiryDate) return -1;
      return a.expiryDate.localeCompare(b.expiryDate);
    });

  const totalActiveCount = allActiveInstances.reduce((sum, i) => sum + i.quantity, 0);
  const hasExpiringItems = expiringItems.length > 0;

  const firstItemWithInstances = expiringItems.find((item) =>
    allInstancesMap.get(item.id)?.some((i) => i.status === 'active')
  );

  const handleBulkAction = (action: 'consume' | 'waste') => {
    if (allActiveInstances.length > 0 && firstItemWithInstances) {
      setBulkInstances({
        instances: allActiveInstances,
        itemId: firstItemWithInstances.id,
        unit: firstItemWithInstances.unit,
      });
      if (action === 'consume') {
        setShowBulkConsumeDialog(true);
      } else {
        setShowBulkWasteDialog(true);
      }
    }
  };

  const handleInstancesLoaded = useCallback((itemId: string, instances: ItemInstance[]) => {
    setAllInstancesMap((prev) => {
      const next = new Map(prev);
      next.set(itemId, instances);
      return next;
    });
  }, []);

  // Group items by urgency
  const expiredItems = expiringItems.filter((item) => {
    const days = getDaysUntilExpiry(item.earliestExpiryDate);
    return days !== null && days < 0;
  });
  const todayItems = expiringItems.filter((item) => {
    const days = getDaysUntilExpiry(item.earliestExpiryDate);
    return days !== null && days >= 0 && days <= 1;
  });
  const soonItems = expiringItems.filter((item) => {
    const days = getDaysUntilExpiry(item.earliestExpiryDate);
    return days !== null && days > 1;
  });

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-semibold text-lg text-foreground">
            <Trans>Needs Attention</Trans>
          </h2>
          <p className="text-sm text-muted-foreground">
            {hasExpiringItems ? (
              <Trans>
                <Plural value={expiringItems.length} one="# item" other="# items" /> to review
              </Trans>
            ) : (
              <Trans>All items are fresh</Trans>
            )}
          </p>
        </div>
        {hasExpiringItems && (totalActiveCount > 0 || isDemoMode) && (
          <div className="flex gap-2" data-tour="bulk-actions">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkAction('consume')}
              disabled={isDemoMode}
              className="text-fresh border-fresh/30 hover:bg-fresh/10"
            >
              <CheckIcon className="h-4 w-4 mr-1" />
              <Trans>Use All</Trans>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkAction('waste')}
              disabled={isDemoMode}
              className="text-expired border-expired/30 hover:bg-expired/10"
            >
              <XMarkIcon className="h-4 w-4 mr-1" />
              <Trans>Waste All</Trans>
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {isLoading ? (
          <Card>
            <CardContent className="p-4">
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-muted/30">
                    <div className="h-10 w-10 animate-pulse rounded-lg bg-muted" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-32 animate-pulse rounded bg-muted" />
                      <div className="h-3 w-20 animate-pulse rounded bg-muted" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-destructive">
                <Trans>Failed to load expiring items</Trans>
              </p>
            </CardContent>
          </Card>
        ) : hasExpiringItems ? (
          <>
            {/* Expired Section */}
            {expiredItems.length > 0 && (
              <TimelineSection
                title={t`Expired`}
                items={expiredItems}
                householdId={householdId}
                onInstancesLoaded={handleInstancesLoaded}
                variant="expired"
                isDemoMode={isDemoMode}
                isFirstSection={true}
              />
            )}

            {/* Today Section */}
            {todayItems.length > 0 && (
              <TimelineSection
                title={t`Today & Tomorrow`}
                items={todayItems}
                householdId={householdId}
                onInstancesLoaded={handleInstancesLoaded}
                variant="today"
                isDemoMode={isDemoMode}
                isFirstSection={expiredItems.length === 0}
              />
            )}

            {/* Soon Section */}
            {soonItems.length > 0 && (
              <TimelineSection
                title={
                  dashboardExpiryDays && dashboardExpiryDays !== 7
                    ? t`Within ${dashboardExpiryDays} days`
                    : t`This Week`
                }
                items={soonItems}
                householdId={householdId}
                onInstancesLoaded={handleInstancesLoaded}
                variant="soon"
                isDemoMode={isDemoMode}
                isFirstSection={expiredItems.length === 0 && todayItems.length === 0}
              />
            )}
          </>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-fresh/10 flex items-center justify-center mb-3">
                <CheckIcon className="h-6 w-6 text-fresh" />
              </div>
              <p className="font-medium text-foreground">
                <Trans>All items are fresh!</Trans>
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                <Trans>No items expiring in the next {dashboardExpiryDays} days.</Trans>
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bulk Dialogs */}
      {showBulkConsumeDialog && bulkInstances && (
        <QuantityActionDialog
          householdId={householdId}
          itemId={bulkInstances.itemId}
          instances={bulkInstances.instances}
          unit={bulkInstances.unit}
          action="consume"
          onClose={() => {
            setShowBulkConsumeDialog(false);
            setBulkInstances(null);
          }}
        />
      )}

      {showBulkWasteDialog && bulkInstances && (
        <QuantityActionDialog
          householdId={householdId}
          itemId={bulkInstances.itemId}
          instances={bulkInstances.instances}
          unit={bulkInstances.unit}
          action="waste"
          onClose={() => {
            setShowBulkWasteDialog(false);
            setBulkInstances(null);
          }}
        />
      )}
    </>
  );
}
